import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { BorderedSection } from '../../../components';
import TitleBar from '../../../components/TitleBar';
import { Comment } from '../../../components/assets';
import { Footer } from '../../../components/common/Footer';
import { useMutation, useQuery } from '@apollo/client';
import {
  FindDependenciesByLocation,
  FindDependenciesByLocationWithoutChildren,
} from './processesTypes';

import './processesAndEquipment.css';
import CommentsForm from './CommentsForm';
import { cleanFinancials, tableSortingMethods } from '../../../utils/';
import {
  FIND_DEPENDENCIES_BY_LOCATION,
  FIND_DEPENDENCIES_BY_LOCATION_PROCESS_AREAS,
  UPSERT_MULTIPLE_PROCESS_DEPENDENCY,
} from '../queries/processesAndEquipment';
import AntModal from '../../../components/generic/AntModal';
import FinancialData from '../../../components/common/FinancialData';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../components/generic/Table/TableV3';
import { TableColumnsType } from 'antd';

import { camelCase } from 'lodash';
import DependenciesTitle from '../../../components/common/DependenciesTitle';
import { useUserContext } from '../../../context/PermissionsContext';
import { ExportColumn } from '../../../interfaces/ExportColumn';
import { baseColumns } from './exportColumns';
import QueryErrorBoundary from '../../../components/error-handling';
import TitleUpdater from '../../../components/common/TitleUpdater';

type processesColumns = {
  title: string;
  dataIndex?: string;
  key: string;
  align?: string;
  width?: number;
  fixed?: string;
  render: (_: any, record: FindDependenciesByLocation) => JSX.Element;
}[];

const COLUMNS_PER_PAGE = 7;

const ProcessesAndEquipment = () => {
  const params = useParams();
  const { locationId, processAreaId } = params;
  let [searchParams] = useSearchParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [itemToUpdate, setitemToUpdate] =
    useState<FindDependenciesByLocationWithoutChildren | null>(null);
  const [isEditing, setisEditing] = useState(false);

  const recordType = searchParams.get('type');
  const processAreaIdProcess = searchParams.get('processAreaId');

  const locationName = searchParams.get('locationName') ?? '';
  const processAreaName = searchParams.get('processArea') ?? '';

  const { canEdit } = useUserContext();

  const variables =
    recordType === 'processArea'
      ? {
          locationUUID: locationId,
          processAreaUUID: processAreaId,
          processUUID: '',
        }
      : {
          locationUUID: locationId,
          processAreaUUID: processAreaIdProcess,
          processUUID: processAreaId,
        };

  const query =
    recordType === 'processArea'
      ? FIND_DEPENDENCIES_BY_LOCATION_PROCESS_AREAS
      : FIND_DEPENDENCIES_BY_LOCATION;

  const { data, loading, error } = useQuery(query, {
    variables,
  });

  const processesEquipmentData = data?.findAll || [];

  const [updateProcesses, { loading: updateProcessLoading }] = useMutation(
    UPSERT_MULTIPLE_PROCESS_DEPENDENCY,
    {
      refetchQueries: ['FindDependenciesByLocation'],
    },
  );

  // NOTE: code bellow commented by Chris (this fixes table collapsing issues)

  // const allData = data?.findAll.flatMap((item: any) => {
  //   const intermediates = item.intermediates.map((intermediate: any) => ({
  //     ...intermediate,
  //   }));
  //   return [...item.children, ...intermediates];
  // });

  // const divisionsToBeFiltered = data?.findAll.map((division: any) => ({
  //   ...division,
  //   children: allData,
  // }));

  const dependencies = cleanFinancials(
    processesEquipmentData,
  ) as unknown as FindDependenciesByLocation[];

  const handleEdit = (record: FindDependenciesByLocation) => {
    setisEditing(true);
    setModalOpen(true);
    const { children, ...rest } = record;
    setitemToUpdate(rest);
  };

  const handleAddComment = () => {
    setisEditing(false);
    setModalOpen(true);
    const { children, ...rest } = data?.findAll[0]!;
    setitemToUpdate(rest);
  };

  const processesColumns: any = (
    dependencies[0]?.procesessDependencys || []
  )?.map((ProcesessDependency: any, index: number) => ({
    title: (
      <DependenciesTitle
        name={ProcesessDependency.process}
        comment={ProcesessDependency.comment}
      />
    ),
    dataIndex: 'procesessDependencys',
    key: ProcesessDependency.key,
    colIndex: index,
    editable: true,
    width: 150,
    align: 'right',
    sorter: (a: any, b: any) =>
      tableSortingMethods.number(
        a.processesDependencys.find(
          (process: any) => process.process === ProcesessDependency.process,
        )?.percentage,
        b.processesDependencys.find(
          (process: any) => process.process === ProcesessDependency.process,
        )?.percentage,
      ),
    showSorterTooltip: false,
    render: (_: any, record: FindDependenciesByLocation) => {
      return (
        <div className="custom-table--column text-sm">
          <span
            className={`font-semibold ${
              record?.procesessDependencys?.find(
                (process) =>
                  process.processUUID === ProcesessDependency.processUUID,
              )?.percentage === 0
                ? 'text-secondary-200'
                : ''
            }`}
          >
            {record?.procesessDependencys
              ?.find(
                (process) =>
                  process.processUUID === ProcesessDependency.processUUID,
              )
              ?.percentage.toFixed(0)}
            {'%'}
          </span>
          <br />
          <span
            className={`font-semibold
                            ${
                              record?.procesessDependencys?.find(
                                (process) =>
                                  process.processUUID ===
                                  ProcesessDependency.processUUID,
                              )?.supportedRevenue === 0
                                ? 'text-secondary-200'
                                : ''
                            }`}
          >
            {
              <FinancialData
                text={
                  record?.procesessDependencys?.find(
                    (process) =>
                      process.processUUID === ProcesessDependency.processUUID,
                  )?.supportedRevenue!
                }
              />
            }
          </span>
        </div>
      );
    },
  }));

  const columns: TableColumnsType<any> = [
    {
      title: '',
      dataIndex: 'name',
      key: 'name',
      width: 240,
      fixed: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (record: any) => (
        <span className="custom-table--column">{record}</span>
      ),
    },
    {
      title: 'Revenue Supported  Annually',
      dataIndex: 'supportedRevenue',
      key: 'supportedRevenue',
      width: 100,
      fixed: 'left',
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.supportedRevenue, b.supportedRevenue),
      render: (_: any, record: any) => (
        <span className="custom-table--column custom-table--column-revenue">
          {<FinancialData text={record.supportedRevenue} />}
        </span>
      ),
    },
    ...processesColumns,
  ];

  const formFields = dependencies[0]?.procesessDependencys?.map(
    (process: any) => ({
      label: process.process,
      key: camelCase(process.process),
      type: 'percentage',
      defaultValue: process.percentage,
    }),
  );

  const modifyVariables = ({ currentRecord }: ModifyVariablesArgs) => {
    const { __typename } = currentRecord;

    switch (__typename) {
      case 'DependencyMatrixModel':
        return {
          divisionFinancial: { uuid: currentRecord.key },
          location: { uuid: locationId },
          processArea: { uuid: processAreaId },
        };
      case 'ProcessProductGroups':
        return {
          productGroup: { uuid: currentRecord.key },
          location: { uuid: locationId },
          processArea: { uuid: processAreaId },
        };
      case 'ProcessProducts':
        return {
          product: { uuid: currentRecord.key },
          location: { uuid: locationId },
          processArea: { uuid: processAreaId },
        };
      default:
        return {
          intermediate: { uuid: currentRecord.key },
          location: { uuid: locationId },
          processArea: { uuid: processAreaId },
        };
    }
  };

  const mappingColumns: ExportColumn[] = [];

  if (dependencies.length > 0) {
    const mappingPercentages = dependencies[0]?.procesessDependencys?.map(
      (process: any, index: number) => ({
        key: process.processUUID,
        header: process.process,
        type: 'percentage',
        nestedKey: `procesessDependencys.${index}.percentage`,
      }),
    );
    mappingColumns.push(...(mappingPercentages as any));
  }

  return (
    <>
      <TitleUpdater title={`${locationName}/Processes & Equipment Mapping`} />
      <TitleBar
        title="Dependency Mapping"
        subtitle={`${locationName} // ${processAreaName} // Processes & Equipment`}
        brief={`In the matrix, enter the degree to which each division, product group, product, or intermediate depends on the processes within the ${searchParams.get(
          'processArea',
        )} process area.`}
      />
      <QueryErrorBoundary error={error}>
        <BorderedSection
          title="Processes & Equipment Dependencies"
          noPadding
          extendOptions={
            canEdit ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleAddComment}
                  className="text-blue underline hover:cursor-pointer font-normal"
                >
                  Add / Edit Commentary
                </button>
                <Comment />
              </div>
            ) : null
          }
        >
          <TableV3
            title="Processes & Equipment Dependencies"
            columns={columns as any}
            data={data?.findAll}
            loading={loading || updateProcessLoading}
            editable
            updateGQL={updateProcesses}
            formFields={formFields}
            modifyVariables={modifyVariables}
            inlineEditing
            useBorderedSection={false}
            editableOptions={{
              create: false,
              update: true,
              delete: false,
            }}
            isMatrix
            matrixType="processes"
            scroll={{ x: 800, y: 340 }}
            customStyles="custom-table--dependency-mapping nested-columns--custom"
            exportExcelColumns={[...baseColumns, ...mappingColumns]}
          />
        </BorderedSection>
      </QueryErrorBoundary>
      <Footer
        backPath={`/location-selection/${locationId}/process-areas-summary?locationId=${locationId}&locationName=${searchParams.get(
          'locationName',
        )}`}
        nextPath={`/location-selection/`}
        goNextText="Back to Locations"
      />
      <AntModal
        openModal={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={
          isEditing ? 'Process Dependency' : 'Process Dependency Commentary'
        }
        width={750}
      >
        <CommentsForm
          data={itemToUpdate!}
          onCancel={() => setModalOpen(false)}
          isEditing={isEditing}
          locationId={locationId}
          processAreaId={processAreaId!}
        />
      </AntModal>
    </>
  );
};

export default ProcessesAndEquipment;
